import React, { useMemo, useCallback, useState } from 'react';
import { Users, AlertCircle } from 'lucide-react';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useInfiniteUsers, UsersFilters } from '@/hooks/queries/useUsers';
import { useTranslation } from '@/hooks/useTranslation';
import UserCard from './UserCard';
import UserCardSkeleton from './UserCardSkeleton';
import UserDetailsDrawer from './UserDetailsDrawer';

interface PeopleContentProps {
  filters: UsersFilters;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

const PeopleContent: React.FC<PeopleContentProps> = ({ filters, hasActiveFilters, onClearFilters }) => {
  const { t } = useTranslation('people');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } = useInfiniteUsers(filters);

  // Sort users alphabetically by full name on frontend
  const users = useMemo(() => {
    const allUsers =
      data?.pages.flatMap((page) => {
        return page.data || [];
      }) || [];

    return allUsers.sort((a, b) => {
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [data]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loaderRef = useInfiniteScroll({
    hasMore: !!hasNextPage && !isFetchingNextPage,
    onLoadMore: handleLoadMore,
  });

  const handleUserClick = useCallback((userId: string) => {
    setSelectedUserId(userId);
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedUserId(null);
  }, []);

  return (
    <>
      {/* Error State */}
      {error && (
        <div className="mb-8 bg-red-50 dark:bg-red-950/30 border-l-4 border-red-500 rounded-xl p-6 flex items-start gap-4 shadow-lg backdrop-blur-sm">
          <div className="flex-shrink-0 p-2 rounded-lg bg-red-100 dark:bg-red-900/50">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-red-900 dark:text-red-200 mb-1">{t('states.error.title')}</h3>
            <p className="text-sm text-red-700 dark:text-red-300">{error.message || t('states.error.message')}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold"
            >
              {t('states.error.retry')}
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && users.length === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <UserCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && users.length === 0 && !error && (
        <div className="relative overflow-hidden bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-800/50 p-16 text-center shadow-xl">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5" />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-950 dark:to-purple-950 mb-6">
              <Users className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{t('states.empty.title')}</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              {hasActiveFilters ? t('states.empty.withFilters') : t('states.empty.withoutFilters')}
            </p>
            {hasActiveFilters && (
              <button
                onClick={onClearFilters}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('states.empty.clearFilters')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Users Grid */}
      {users.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {users.map((user) => (
              <UserCard key={user.id} user={user} onClick={() => handleUserClick(user.id)} />
            ))}
          </div>

          {/* Infinite Scroll Loader */}
          {hasNextPage && (
            <div ref={loaderRef} className="mt-12">
              {isFetchingNextPage && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <UserCardSkeleton key={`skeleton-${i}`} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* End of Results */}
          {!hasNextPage && users.length > 0 && (
            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl rounded-full border border-gray-200/50 dark:border-gray-800/50 shadow-lg">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('states.endOfResults')}</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* User Details Drawer */}
      <UserDetailsDrawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} userId={selectedUserId} />
    </>
  );
};

export default PeopleContent;

